import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Grid2,
  Typography,
  Container,
  Chip,
  styled,
} from "@mui/material";
import { ArrowUpRight, Clock, Tag } from "lucide-react";

import TBlog from "@utils/blog";

const MetaInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  color: "text.secondary",
  fontSize: "11px",
});

type TBlogProps = {
  blogs: typeof TBlog;
};

const Blog: React.FC<TBlogProps> = ({ blogs }) => {
  return (
    <Box id="Blog" sx={{ py: 6 }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            color: "text.primary",
            fontWeight: 600,
            position: "relative",
            display: "inline-block",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-4px",
              left: 0,
              width: "100%",
              height: "2px",
              background: "linear-gradient(to right, #536493, transparent)",
            },
          }}
        >
          My Blog Posts
        </Typography>

        <Grid2 container spacing={4} display={"flex"} justifyContent={"center"}>
          {blogs.map((blog) => (
            <Grid2 key={blog.title} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "none",
                  backgroundColor: "transparent",
                  transition: "border-color 0.2s ease",
                  "&:hover": {
                    borderColor: "#536493",
                  },
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    sx={{ height: 140 }}
                    image={blog.image}
                    title={blog.title}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 2,
                    }}
                  >
                    <Chip
                      label={blog.language}
                      size="small"
                      sx={{
                        height: "22px",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        backdropFilter: "blur(4px)",
                        "& .MuiChip-label": {
                          px: 1,
                          fontSize: "11px",
                          fontWeight: 600,
                        },
                        color: "#536493",
                      }}
                      icon={<Tag size={12} />}
                    />
                  </Box>
                </Box>

                <CardContent sx={{ p: 2, pt: 1.5 }}>
                  <Typography
                    variant="body1"
                    component="h2"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      lineHeight: 1.4,
                      mb: 0.5,
                    }}
                  >
                    {blog.title}
                  </Typography>
                  <MetaInfo>
                    <Clock size={12} />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "11px", color: "text.secondary" }}
                    >
                      {blog.minReadTime || "5"} mins read
                    </Typography>
                  </MetaInfo>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0, justifyContent: "center" }}>
                  <Button
                    endIcon={<ArrowUpRight size={16} />}
                    target="_blank"
                    href={blog.url}
                    variant="contained"
                    sx={{
                      backgroundColor: "#536493",
                      boxShadow: "none",
                      textTransform: "none",
                      padding: "4px 12px",
                      fontWeight: 500,
                      fontSize: "13px",
                      "&:hover": {
                        backgroundColor: "#647aa8",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    Read
                  </Button>
                </CardActions>
              </Card>
            </Grid2>
          ))}
        </Grid2>
      </Container>
    </Box>
  );
};

export default Blog;
